import { RouterProvider, useLocation, Outlet } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Toaster } from "sonner";

import { privateRoutes, publicRoutes } from "./routes";
import PrivateLayout from "@layouts/PrivateLayout";
import PublicLayout from "@layouts/PublicLayout";
import ErrorBoundary from "./exceptionHandlers/ErrorBoundry";
import { useEffect } from "react";
import { initSmartlook } from "./smartLook";
import {
  createSentryBrowserRouter,
  initSentryReactRouter,
} from "./utils/sentryReactRouter";
import RequirePermission from "@/shared/RequirePermission";
import InitialRedirect from "@/shared/InitialRedirect";

import AppDataLoader from "@/components/AppDataLoader";
import UnsavedChangesToast from "@shared/UnsavedChangesToast";

const smartLookKey = import.meta.env.VITE_SMARTLOOK_ID;

// Initialize Sentry React Router integration
initSentryReactRouter();

const ConditionalAppDataLoader: React.FC = () => {
  const location = useLocation();
  const publicPaths = publicRoutes.map((route) => route.path);
  const isPublicRoute = publicPaths.includes(location.pathname);

  if (isPublicRoute) {
    return <Outlet />;
  }

  return (
    <AppDataLoader>
      <Outlet />
    </AppDataLoader>
  );
};

// Create router with Sentry instrumentation
const router = createSentryBrowserRouter([
  {
    path: "/",
    element: <ConditionalAppDataLoader />,
    children: [
      {
        index: true,
        element: (
          <PrivateLayout>
            <ErrorBoundary>
              <InitialRedirect />
            </ErrorBoundary>
          </PrivateLayout>
        ),
      },
      ...privateRoutes.map((route) => {
        const requires = (route as any).permission;
        const children = <route.component />;
        const wrapped = requires ? (
          <RequirePermission
            resource={requires.resource}
            action={requires.action}
            subResource={requires.subResource}
            allOf={requires.allOf}
            anyOf={requires.anyOf}
          >
            {children}
          </RequirePermission>
        ) : (
          children
        );

        return {
          path: route.path,
          element: (
            <PrivateLayout>
              <ErrorBoundary>{wrapped}</ErrorBoundary>
              <UnsavedChangesToast />
            </PrivateLayout>
          ),
        };
      }),
      ...publicRoutes.map((route) => {
        return {
          path: route.path,
          element: (
            <PublicLayout>
              <ErrorBoundary>
                <route.component />
              </ErrorBoundary>
            </PublicLayout>
          ),
        };
      }),
    ],
    errorElement: (
      <ErrorBoundary>
        <div>Navigation Error</div>
      </ErrorBoundary>
    ),
  },
]);

const App = () => {
  useEffect(() => {
    if (smartLookKey) {
      initSmartlook(smartLookKey);
    }
  }, []);

  return (
    <>
      <RouterProvider router={router} />
      <ToastContainer />
      <Toaster position="top-right" richColors />
    </>
  );
};

export default App;
